import StockDocumentTotals from "./StockDocumentTotals";
// src/components/bon_consum/Form.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DocumentToolbar from "../common/DocumentToolbar";
import useDocumentDraft from "../../hooks/useDocumentDraft";
import { Row, Col, Table, Button, Form, Alert, Spinner } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import AsyncSelect from "react-select/async";
import { authFetch } from "../utils/authFetch";
import StocCurentPickerModal from "../pickers/StocCurentPickerModal";

// helpers
const toISODateTimeLocal = (d) => {
  if (!d) return "";
  const dt = d instanceof Date ? d : new Date(d);
  if (isNaN(dt.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return (
    dt.getFullYear() +
    "-" +
    pad(dt.getMonth() + 1) +
    "-" +
    pad(dt.getDate()) +
    "T" +
    pad(dt.getHours()) +
    ":" +
    pad(dt.getMinutes()) +
    ":" +
    pad(dt.getSeconds())
  );
};

const num = (v) => {
  const x = parseFloat(String(v ?? "").replace(",", "."));
  return Number.isFinite(x) ? x : 0;
};

const makeUid = () => {
  // crypto.randomUUID() e ok în majoritatea browserelor moderne
  if (typeof crypto !== "undefined" && crypto.randomUUID)
    return crypto.randomUUID();
  return `uid_${Date.now()}_${Math.random().toString(16).slice(2)}`;
};

const emptyLine = () => ({
  uid: makeUid(),
  id: null,
  bxpart_id: null,
  part_label: "",
  qty: "",

  available: null,
  loadingAvail: false,
});

async function fetchFirstOk(urls) {
  let lastErr = null;

  for (const u of urls) {
    try {
      const r = await authFetch(u);

      if (r && typeof r === "object" && !("ok" in r) && !("status" in r)) {
        return { __json: r };
      }

      if (r && typeof r === "object" && "ok" in r) {
        if (r.ok) return r;

        let body = "";
        if (typeof r.text === "function") {
          try {
            body = await r.text();
          } catch {}
        }
        console.warn("fetchFirstOk not ok", u, r.status, body?.slice?.(0, 300));
      }
    } catch (e) {
      lastErr = e;
      console.warn("fetchFirstOk failed", u, e);
    }
  }

  console.warn("fetchFirstOk none ok", lastErr);
  return null;
}

export function BonConsumForm({ initialDoc, onSubmit, busy, isClosed, onChangePayload, recType = 3, documentActions }) {
  const formElement = useRef(null);
  const [err, setErr] = useState("");
  const [centerConfig, setCenterConfig] = useState(null);
  const [centerError, setCenterError] = useState('');
  const [costCenterId, setCostCenterId] = useState(initialDoc?.cost_center_id || '');
  useEffect(() => {
    if (recType !== 3) return;
    let active = true;
    authFetch('/repartizari-centre/config').then(data => { if (active) setCenterConfig(data); })
      .catch(e => { if (active) setCenterError(e.message); });
    return () => { active = false; };
  }, [recType]);
  useEffect(() => { setCostCenterId(initialDoc?.cost_center_id || ''); }, [initialDoc?.id, initialDoc?.cost_center_id]);
  const [showPartModal, setShowPartModal] = useState(false);
  const [partLineUid, setPartLineUid] = useState(null);

  const openPartForLine = (uid) => {
    if (!selGestiune?.value) {
      setErr("Selectează gestiunea mai întâi.");
      return;
    }
    setPartLineUid(uid);
    setShowPartModal(true);
  };

  const handlePickPart = (opt) => {
    if (!partLineUid) return;
    handlePartChange(partLineUid, { value: opt.id, label: opt.label, raw: opt.raw });
    setShowPartModal(false);
    setPartLineUid(null);
  };

  const [selContact, setSelContact] = useState(null);

  // header
  const [docNo, setDocNo] = useState(
    initialDoc?.bxdoc_no || initialDoc?.doc_no || "",
  );
  const [docDate, setDocDate] = useState(() => {
    const v = initialDoc?.bxdoc_date || initialDoc?.doc_date;
    return v ? new Date(v) : new Date();
  });

  const [selGestiune, setSelGestiune] = useState(null);
  const [selGestiune2, setSelGestiune2] = useState(null);
  const [selTipBon, setSelTipBon] = useState(null);
  const [observatii, setObservatii] = useState(initialDoc?.observatii || "");

  // lines
  const [lines, setLines] = useState(() => {
    if (recType === 4) {
      const outs = initialDoc?.linii_out || [];
      const ins = initialDoc?.linii || [];
      if (!outs.length) return [emptyLine()];
      return outs.map((out, idx) => {
        const correspondingIn = ins[idx];
        return {
          uid: makeUid(),
          id: out.id ?? null,
          bxstocin_id: out.bxstocin_id ?? null,
          bxpart_id: correspondingIn?.bxpart_id ?? null,
          part_label: correspondingIn?.bxpart_id ? `Part #${correspondingIn.bxpart_id}` : "",
          qty: out.bxqtyscazut ?? "",
          stockPrice: Number(out.bxqtyscazut) && out.bxsuma != null ? Number(out.bxsuma) / Number(out.bxqtyscazut) : correspondingIn?.bxpart_regprice ?? null,
          available: null,
          loadingAvail: false,
        };
      });
    }

    const lns = initialDoc?.linii_lns || initialDoc?.linii || [];
    if (!lns.length) return [emptyLine()];
    return lns.map((x) => ({
      uid: makeUid(),
      id: x.id ?? null,
      bxpart_id: x.bxpart_id ?? null,
      part_label:
        x.part_label ||
        x.denumire ||
        (x.bxpart_id ? `Part #${x.bxpart_id}` : ""),
      qty: x.qty ?? "",
      stockPrice: x.pret_magazie ?? null,

      available: null,
      loadingAvail: false,
    }));
  });

  // ---- SAFE AVAIL CACHE (uid-based)
  const lastAvailKeyRef = useRef(new Map()); // uid -> key
  const reqSeqRef = useRef(new Map()); // uid -> seq

  const makeAvailKey = (gest, dateObj, partId) => {
    const d = dateObj ? toISODateTimeLocal(dateObj).slice(0, 10) : "";
    return `${gest ?? ""}|${d}|${partId ?? ""}`;
  };

  const gestiuneBases = useMemo(() => ["/bxgestiune/"], []);
  const tipBonBases = useMemo(() => ["/tip_bon_iesire"], []);
  const partBases = useMemo(() => ["/bxpart/"], []);

  const fetchGestiuneById = async (id) => {
    if (!id) return null;
    try {
      const r = await authFetch(`/bxgestiune/${id}`);
      const data =
        r && r.__json
          ? r.__json
          : typeof r.json === "function"
            ? await r.json()
            : r;
      const label =
        data?.bxnume || data?.denumire || data?.name || `Gestiune #${id}`;
      return { value: id, label };
    } catch (e) {
      console.warn("fetchGestiuneById failed", id, e);
      return { value: id, label: `Gestiune #${id}` };
    }
  };

  const fetchPartById = async (id) => {
    if (!id) return null;
    try {
      const r = await authFetch(`/bxpart/${id}`);
      const data =
        r && r.__json
          ? r.__json
          : typeof r.json === "function"
            ? await r.json()
            : r;
      const label =
        `${data?.cod || ""} ${data?.denumire || ""}`.trim() ||
        `Part #${id}`;
      return label;
    } catch (e) {
      console.warn("fetchPartById failed", id, e);
      return `Part #${id}`;
    }
  };

  // load initial selects if edit
  useEffect(() => {
    const gId = initialDoc?.bxgestiune_id ?? initialDoc?.bxgestiuneId ?? null;
    console.log("EDIT initialDoc =", initialDoc);
    if (gId && !selGestiune) {
      fetchGestiuneById(gId).then((opt) => {
        if (opt) setSelGestiune(opt);
      });
    }

    const g2Id = initialDoc?.bxgestiune2_id ?? initialDoc?.bxgestiune2Id ?? null;
    if (g2Id && !selGestiune2) {
      fetchGestiuneById(g2Id).then((opt) => {
        if (opt) setSelGestiune2(opt);
      });
    }

    const tId = initialDoc?.tip_bon_iesire_id ?? null;
    if (tId && !selTipBon) {
      setSelTipBon({ value: tId, label: `Tip bon #${tId}` });
    }

    const cId = initialDoc?.contact_id ?? initialDoc?.customer_id ?? null;
    if (cId && !selContact) {
      // ia label real din backend
      fetchContactById(cId).then((opt) => {
        if (opt) setSelContact(opt);
      });
    }
  }, [initialDoc, selGestiune, selGestiune2, selTipBon]);

  // Preload missing part labels when editing
  useEffect(() => {
    let active = true;
    const linesToFetch = lines.filter(
      (ln) => ln.bxpart_id && (!ln.part_label || ln.part_label.startsWith("Part #"))
    );

    if (linesToFetch.length === 0) return;

    const fetchAllLabels = async () => {
      const updatedLines = await Promise.all(
        lines.map(async (ln) => {
          if (ln.bxpart_id && (!ln.part_label || ln.part_label.startsWith("Part #"))) {
            const label = await fetchPartById(ln.bxpart_id);
            return { ...ln, part_label: label };
          }
          return ln;
        })
      );
      if (active) {
        setLines(updatedLines);
      }
    };

    fetchAllLabels();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDoc]);

  async function fetchContactById(id) {
    if (!id) return null;
    try {
      const r = await authFetch(`/contacte/${id}`);
      const data =
        r && r.__json
          ? r.__json
          : typeof r.json === "function"
            ? await r.json()
            : r;
      // adaptează label-ul la câmpul real din backend
      const label =
        data?.bxdenumire || data?.denumire || data?.name || `Contact #${id}`;
      return { value: id, label };
    } catch (e) {
      console.warn("fetchContactById failed", id, e);
      return { value: id, label: `Contact #${id}` };
    }
  }

  const loadGestiuni = async (inputValue) => {
    try {
      const q = (inputValue || "").trim();
      const url = (base) =>
        `${base}?skip=0&limit=20&q=${encodeURIComponent(q)}`;

      const r = await fetchFirstOk(gestiuneBases.map(url));
      if (!r) return [];

      const data = r.__json ? r.__json : await r.json();
      const items = data.items || data.results || data || [];

      return items.map((g) => ({
        value: g.id,
        label: g.bxnume || g.denumire || g.name || g.bxcod || g.cod || `Gestiune #${g.id}`,
      }));
    } catch (e) {
      console.error("loadGestiuni failed", e);
      return [];
    }
  };

  const loadTipBon = async (inputValue) => {
    const q = (inputValue || "").trim();
    const url = (base) => `${base}?skip=0&limit=20&q=${encodeURIComponent(q)}`;

    const r = await fetchFirstOk(tipBonBases.map(url));
    if (!r) return [];

    const data = r.__json ? r.__json : await r.json();
    const items = data.items || data.results || data || [];

    return items.map((t) => ({
      value: t.id,
      label: t.denumire || t.name || `Tip bon #${t.id}`,
      _contd_id: t.contd_id,
    }));
  };

  const loadParts = async (inputValue) => {
    try {
      const q = (inputValue || "").trim();
      const gestId = selGestiune?.value;
      if (!gestId) return [];

      const url = (base) =>
        `${base}?skip=0&limit=20&q=${encodeURIComponent(q)}&gestiune_id=${gestId}`;
      const r = await fetchFirstOk(partBases.map(url));
      if (!r) return [];

      const data = r.__json ? r.__json : await r.json();
      const items = data.items || data.results || data || [];

      return items
        .map((p) => ({
          value: p.id,
          label: `${p.cod || ""} ${p.denumire || ""}`.trim() || `Part #${p.id}`,
        }))
        .filter((o) => o.value != null);
    } catch (e) {
      console.error("loadParts failed", e);
      return [];
    }
  };

  // ---- SAFE FETCH AVAIL (uid-based, ignore stale responses)
  const fetchAvailForLine = async (uid, partId) => {
    const gest = selGestiune?.value ?? null;
    if (!gest || !partId) return;

    // seq token per uid
    const prev = reqSeqRef.current.get(uid) ?? 0;
    const seq = prev + 1;
    reqSeqRef.current.set(uid, seq);

    setLines((prevLines) =>
      prevLines.map((x) => (x.uid === uid ? { ...x, loadingAvail: true } : x)),
    );

    try {
      const as_of = toISODateTimeLocal(docDate);
      const url = `/stock/available?bxpart_id=${partId}&gestiune_id=${gest}&as_of=${encodeURIComponent(
        as_of,
      )}`;

      const r = await authFetch(url);

      // parse
      let qty;
      if (r && r.__json) qty = r.__json;
      else if (r && typeof r.json === "function") qty = await r.json();
      else qty = r;

      const val =
        typeof qty === "number" ? qty : (qty?.qty ?? qty?.available ?? 0);

      // ignore stale responses
      if (reqSeqRef.current.get(uid) !== seq) return;

      setLines((prevLines) =>
        prevLines.map((x) =>
          x.uid === uid ? { ...x, available: val, loadingAvail: false } : x,
        ),
      );
    } catch (e) {
      if (reqSeqRef.current.get(uid) !== seq) return;
      setLines((prevLines) =>
        prevLines.map((x) =>
          x.uid === uid ? { ...x, loadingAvail: false } : x,
        ),
      );
      setErr(String(e?.message || e));
    }
  };

  const handlePartChange = (uid, opt) => {
    const partId = opt?.value ?? null;
    const partLabel = opt?.label ?? "";
    const stocinId = opt?.raw?.bxstocin_id ?? null;
    const availableVal = opt?.raw?.cantitate ?? null;

    setLines((prevLines) =>
      prevLines.map((ln) =>
        ln.uid === uid
          ? {
              ...ln,
              bxpart_id: partId,
              part_label: partLabel,
              bxstocin_id: stocinId,
              available: availableVal,
              stockPrice: opt?.raw?.pret_magazie ?? null,
              loadingAvail: false,
            }
          : ln,
      ),
    );

    if (partId && selGestiune?.value && availableVal == null) {
      // mark key now to prevent immediate duplicate from useEffect
      const key = makeAvailKey(selGestiune.value, docDate, partId);
      lastAvailKeyRef.current.set(uid, key);
      fetchAvailForLine(uid, partId);
    }
  };

  // refetch avail when docDate or gestiune changes (NO DUPLICATES)
  const partSig = useMemo(
    () => lines.map((x) => x.bxpart_id || "").join(","),
    [lines],
  );

  useEffect(() => {
    const gest = selGestiune?.value ?? null;
    if (!gest) return;

    lines.forEach((ln) => {
      const partId = ln?.bxpart_id ?? null;
      if (!partId) return;

      const key = makeAvailKey(gest, docDate, partId);
      const lastKey = lastAvailKeyRef.current.get(ln.uid);
      if (lastKey === key) return;

      lastAvailKeyRef.current.set(ln.uid, key);
      fetchAvailForLine(ln.uid, partId);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selGestiune?.value, docDate, partSig]);

  const addLine = () => setLines((p) => [...p, emptyLine()]);

  const removeLine = (uid) =>
    setLines((p) => (p.length <= 1 ? p : p.filter((x) => x.uid !== uid)));

  const updateLine = (uid, patch) => {
    setLines((prevLines) =>
      prevLines.map((ln) => (ln.uid === uid ? { ...ln, ...patch } : ln)),
    );
  };

  const contactBases = ["/contacte/"];

  const loadContacts = async (inputValue) => {
    try {
      const q = (inputValue || "").trim();
      const url = (base) =>
        `${base}?skip=0&limit=20&q=${encodeURIComponent(q)}`;

      const r = await fetchFirstOk(contactBases.map(url));
      if (!r) return [];

      const data = r.__json ? r.__json : await r.json();
      const items = data.items || data.results || data || [];

      return items.map((c) => ({
        value: c.id,
        label: c.bxdenumire,
      }));
    } catch (e) {
      console.error("loadContacts failed", e);
      return [];
    }
  };

  const buildPayload = useCallback(() => {
    if (recType === 3 && !centerConfig) throw new Error(centerError || 'Se încarcă setările centrelor de cost.');
    if (recType === 3 && centerConfig?.enabled && !costCenterId) throw new Error('Selectează centrul de cost pentru întregul bon de consum.');
    if (!selGestiune?.value) throw new Error("Selectează gestiunea.");
    if (!selContact?.value) throw new Error("Selectează contactul.");
    if (recType === 4) {
      if (!selGestiune2?.value) throw new Error("Selectează gestiunea destinație.");
      if (selGestiune.value === selGestiune2.value) {
        throw new Error("Gestiunea destinație trebuie să fie diferită de gestiunea sursă.");
      }
      if (!selContact?.value) throw new Error("Selectează contactul.");
    } else {
      if (!selTipBon?.value) throw new Error("Selectează tip bon ieșire.");
    }

    let linii;
    if (recType === 4) {
      linii = lines
        .filter((x) => x.bxstocin_id && num(x.qty) > 0)
        .map((x) => ({
          bxstocin_id: x.bxstocin_id,
          bxqtyscazut: num(x.qty),
        }));
      if (!linii.length)
        throw new Error("Adaugă cel puțin o linie validă (produs din stoc + cantitate).");
    } else {
      linii = lines
        .filter((x) => x.bxpart_id && num(x.qty) > 0)
        .map((x) => ({
          id: x.id ?? undefined,
          bxpart_id: x.bxpart_id,
          qty: num(x.qty),
        }));
      if (!linii.length)
        throw new Error("Adaugă cel puțin un produs și o cantitate pozitivă.");
    }

    const payload = {
      rec_type: recType,
      ...(recType === 3 ? { cost_center_id: costCenterId ? Number(costCenterId) : null } : {}),
      bxdoc_no: docNo || undefined,
      bxdoc_date: toISODateTimeLocal(docDate),
      bxgestiune_id: selGestiune.value,
      contact_id: selContact?.value,
      observatii: observatii || "",
      linii,
    };

    if (recType === 4) {
      payload.bxgestiune2_id = selGestiune2.value;
    } else {
      payload.tip_bon_iesire_id = selTipBon.value;
    }

    return payload;
  }, [recType, docNo, docDate, selGestiune, selGestiune2, selTipBon, selContact, observatii, lines, costCenterId, centerConfig, centerError]);

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setErr("");
    try {
      const payload = buildPayload();
      await onSubmit(payload);
    } catch (ex) {
      setErr(String(ex.message || ex));
    }
  };

  const rawPayload = useMemo(() => {
    const payload = {
      rec_type: recType,
      ...(recType === 3 ? { cost_center_id: costCenterId ? Number(costCenterId) : null } : {}),
      bxdoc_no: docNo || "",
      bxdoc_date: toISODateTimeLocal(docDate),
      bxgestiune_id: selGestiune?.value || null,
      contact_id: selContact?.value || null,
      observatii: observatii || "",
    };

    if (recType === 4) {
      payload.bxgestiune2_id = selGestiune2?.value || null;
      payload.linii = lines
        .filter((x) => x.bxstocin_id)
        .map((x) => ({
          bxstocin_id: x.bxstocin_id,
          bxqtyscazut: num(x.qty),
        }));
    } else {
      payload.tip_bon_iesire_id = selTipBon?.value || null;
      payload.linii = lines
        .filter((x) => x.bxpart_id)
        .map((x) => ({
          bxpart_id: x.bxpart_id,
          qty: num(x.qty),
        }));
    }

    return payload;
  }, [recType, docNo, docDate, selGestiune, selGestiune2, selTipBon, selContact, observatii, lines, costCenterId]);

  useEffect(() => {
    if (onChangePayload) {
      onChangePayload(rawPayload);
    }
  }, [rawPayload, onChangePayload]);

  useEffect(() => {
    const handleGlobalSubmit = async () => {
      if (busy || isClosed) return;
      setErr("");
      try {
        const payload = buildPayload();
        await onSubmit(payload);
      } catch (ex) {
        setErr(String(ex.message || ex));
      }
    };
    window.addEventListener("bon-consum-submit", handleGlobalSubmit);
    return () => {
      window.removeEventListener("bon-consum-submit", handleGlobalSubmit);
    };
  }, [onSubmit, buildPayload, busy, isClosed]);

  const draft = useDocumentDraft({ payload: rawPayload, lines: lines.map(l => ({ part: l.bxpart_id, lot: l.bxstocin_id, qty: l.qty })) }, initialDoc);
  return (<>
    {documentActions && <DocumentToolbar {...documentActions} dirty={draft.dirty} busy={busy}
      error={err || documentActions.error} isFinal={!!isClosed}
      onSave={() => formElement.current?.requestSubmit()} finalizeDisabled={draft.dirty} />}
    <Form ref={formElement} onPointerDownCapture={draft.interact} onKeyDownCapture={draft.interact} onChangeCapture={draft.interact}
      onSubmit={event => { if (busy || isClosed) { event.preventDefault(); return; } return handleSubmit(event); }}
      style={isClosed ? { pointerEvents: "none", opacity: 0.6 } : undefined}
    >
      <fieldset disabled={busy || isClosed}>
      {err ? <Alert variant="danger">{err}</Alert> : null}

      <Row className="mb-3">
        <Col md={2}>
          <Form.Label>Nr</Form.Label>
          <Form.Control
            value={docNo}
            onChange={(e) => setDocNo(e.target.value)}
            placeholder="Nr bon"
          />
          {!initialDoc && [3, 4].includes(recType) && <Form.Text>Lasă gol pentru numerotarea automată din Serii documente.</Form.Text>}
        </Col>

        <Col md={2}>
          <Form.Label>Data</Form.Label>
          <div>
            <DatePicker
              disabled={busy || isClosed}
              selected={docDate}
              onChange={(d) => setDocDate(d || new Date())}
              dateFormat="dd.MM.yyyy"
              className="form-control"
            />
          </div>
        </Col>

        <Col md={recType === 4 ? 4 : 5}>
          <Form.Label>{recType === 4 ? "Gestiune sursă" : "Gestiune"}</Form.Label>
          <AsyncSelect
            isDisabled={busy || isClosed}
            cacheOptions
            defaultOptions
            value={selGestiune}
            loadOptions={loadGestiuni}
            onChange={setSelGestiune}
            placeholder="Alege gestiunea..."
          />
        </Col>

        {recType === 4 ? (
          <Col md={4}>
            <Form.Label>Gestiune destinație</Form.Label>
            <AsyncSelect
              isDisabled={busy || isClosed}
              cacheOptions
              defaultOptions
              value={selGestiune2}
              loadOptions={loadGestiuni}
              onChange={setSelGestiune2}
              placeholder="Alege gestiunea destinație..."
            />
          </Col>
        ) : (
          <Col md={3}>
            <Form.Label>Tip bon ieșire</Form.Label>
            <AsyncSelect
              isDisabled={busy || isClosed}
              cacheOptions
              defaultOptions
              value={selTipBon}
              loadOptions={loadTipBon}
              onChange={setSelTipBon}
              placeholder="Alege tip bon..."
            />
          </Col>
        )}
      </Row>

      <Row className="mb-3">
        <Col md={5}>
          <Form.Label>Contact</Form.Label>
          <AsyncSelect
            isDisabled={busy || isClosed}
            cacheOptions
            defaultOptions
            value={selContact}
            loadOptions={loadContacts}
            onChange={setSelContact}
            placeholder="Alege contact..."
          />
        </Col>
      </Row>

      {recType === 3 && centerError && <Alert variant="danger">Nu s-au putut încărca centrele de cost: {centerError}</Alert>}
      {recType === 3 && centerConfig?.enabled && <Row className="mb-3">
        <Col md={6}>
          <Form.Label htmlFor="bon-consum-centru">Centru de cost *</Form.Label>
          <Form.Select id="bon-consum-centru" value={costCenterId} disabled={busy || isClosed} required
            onChange={e => setCostCenterId(e.target.value)}>
            <option value="">Alege centrul de cost</option>
            {centerConfig.centers.map(c => <option key={c.id} value={c.id}>{c.code} — {c.name}</option>)}
          </Form.Select>
          <Form.Text>Toate cheltuielile acestui bon se repartizează automat 100% pe centrul ales.</Form.Text>
        </Col>
      </Row>}
      <Row className="mb-3">
        <Col>
          <Form.Label>Observații</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={observatii}
            onChange={(e) => setObservatii(e.target.value)}
          />
        </Col>
      </Row>

      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="m-0">
          {recType === 4 ? "Linii bon transfer" : "Linii bon consum"}
        </h5>
        <Button variant="outline-primary" onClick={addLine} disabled={busy}>
          + Linie
        </Button>
      </div>

      <Table bordered size="sm" className="align-middle">
        <thead>
          <tr>
            <th style={{ width: 480 }}>Produs</th>
            <th style={{ width: 140 }} className="text-end">
              Disponibil
            </th>
            <th style={{ width: 120 }} className="text-end">
              Cantitate
            </th>

            <th style={{ width: 90 }} className="text-center">
              Acțiuni
            </th>
          </tr>
        </thead>
        <tbody>
          {lines.map((ln) => (
            <tr key={ln.uid}>
              <td>
                <div
                  role="button"
                  onClick={() => !isClosed && openPartForLine(ln.uid)}
                  className={`header-clickable w-100 ${isClosed ? "disabled" : ""}`}
                  title={isClosed ? "" : "Click pentru a alege produs"}
                  style={{ cursor: isClosed ? "default" : "pointer" }}
                >
                  <Form.Control
                    readOnly
                    disabled={isClosed}
                    value={ln.part_label || ""}
                    placeholder="Click pentru a alege produs..."
                  />
                </div>
              </td>

              <td className="text-end">
                {ln.loadingAvail ? (
                  <Spinner animation="border" size="sm" />
                ) : ln.available == null ? (
                  <span className="text-muted">—</span>
                ) : (
                  <strong>{Number(ln.available).toFixed(3)}</strong>
                )}
              </td>

              <td className="text-end">
                <Form.Control
                  value={ln.qty}
                  onChange={(e) => updateLine(ln.uid, { qty: e.target.value })}
                  className="text-end"
                  placeholder="0"
                />
              </td>

              <td className="text-center">
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => removeLine(ln.uid)}
                  disabled={busy || lines.length <= 1}
                >
                  ✕
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <StockDocumentTotals lines={lines} docId={initialDoc?.id} recType={recType} closed={isClosed} />

      {!documentActions && <div className="d-flex justify-content-end gap-2 mt-3">
        <Button type="submit" disabled={busy}>
          {busy ? "Salvez..." : "Salvează"}
        </Button>
      </div>}

      <StocCurentPickerModal
        show={showPartModal}
        onHide={() => {
          setShowPartModal(false);
          setPartLineUid(null);
        }}
        onPick={handlePickPart}
        gestiuneId={selGestiune?.value}
      />
      </fieldset>
    </Form></>
  );
}
